/**
 * Handle Properties Panel
 * Shows properties when a handle is clicked
 */

import React from 'react';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { getInputPorts, getOutputPorts } from '@flow-studio/types';
import { ArrowRightIcon, ArrowLeftIcon } from '@bizfirst/common-react';

const HANDLE_STYLES = ['circle', 'square', 'diamond', 'arrow', 'triangle'] as const;
const POSITIONS = ['left', 'right', 'top', 'bottom'] as const;

export const HandleProperties: React.FC = () => {
  const { nodes, edges, updateNode } = useWorkflowStore();
  const { selectedHandle, setSelectedHandle, theme } = useUIStore();

  if (!selectedHandle) {
    return null;
  }

  const { nodeId, handleId, handleType } = selectedHandle;

  const node = nodes.find((n) => n.id === nodeId);

  const handle = handleType === 'source'
    ? (node?.data.processElement ? getOutputPorts(node.data.processElement) : []).find((p) => p.portKey === handleId)
    : (node?.data.processElement ? getInputPorts(node.data.processElement) : []).find((p) => p.portKey === handleId);

  const connections = edges.filter((edge) => {
    if (handleType === 'source') {
      return edge.source === nodeId && edge.sourceHandle === handleId;
    } else {
      return edge.target === nodeId && edge.targetHandle === handleId;
    }
  });

  const connectedNodes = connections.map((conn) => {
    const connectedNodeId = handleType === 'source' ? conn.target : conn.source;
    const connectedNode = nodes.find((n) => n.id === connectedNodeId);
    return {
      id: connectedNodeId,
      name: connectedNode?.data.processElement?.displayName || connectedNodeId,
      handleId: handleType === 'source' ? conn.targetHandle : conn.sourceHandle,
    };
  });

  const handleClose = () => {
    setSelectedHandle(null);
  };

  if (!node || !handle) {
    return null;
  }

  const effectiveShape = handle.handleStyle || (handle.isErrorPort ? 'triangle' : (handleType === 'target' ? 'arrow' : 'circle'));

  const updatePort = (changes: Record<string, any>) => {
    const currentPorts: any[] = (node.data.processElement as any)?.ports ?? [];
    const updatedPorts = currentPorts.map((p) =>
      p.portKey === handle.portKey ? { ...p, ...changes } : p
    );
    updateNode(nodeId, {
      data: {
        ...node.data,
        processElement: { ...(node.data.processElement as any), ports: updatedPorts },
      },
    });
  };

  const themeClass = theme === 'light' ? 'light' : '';
  const handleTypeClass = handleType === 'source' ? 'source' : 'target';
  const badgeClass = handleType === 'source' ? 'source' : 'target';

  // Shared style for all editable controls — looks like property-value without nesting
  const controlStyle: React.CSSProperties = {
    width: '100%',
    fontSize: '13px',
    color: theme === 'dark' ? 'rgba(220, 225, 222, 0.9)' : '#101211',
    background: theme === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#f9fafb',
    border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.07)' : '#e5e7eb'}`,
    borderRadius: '4px',
    padding: '4px 8px',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  };

  return (
    <div className={`properties-panel ${themeClass}`}>
      <div className={`properties-header ${handleTypeClass}`}>
        <div className="properties-header-content">
          <div className="properties-title">
            {handleType === 'source' ? <ArrowRightIcon size={14} /> : <ArrowLeftIcon size={14} />} Handle Properties
          </div>
          <div className="properties-subtitle">{node.data.processElement?.displayName}</div>
        </div>
        <button className="properties-close-button" onClick={handleClose} title="Close">
          ✕
        </button>
      </div>

      <div className="properties-body">
        {/* Handle Name — editable */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Handle Name</div>
          <input
            type="text"
            value={handle.label || handleId}
            onChange={(e) => updatePort({ label: e.target.value })}
            style={{ ...controlStyle, cursor: 'text' }}
          />
        </div>

        {/* Handle ID — read-only */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Handle ID</div>
          <div className={`property-value ${themeClass}`}>{handleId}</div>
        </div>

        {/* Type — editable */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Type</div>
          <select
            value={handle.portType || (handleType === 'target' ? 'input' : 'output')}
            onChange={(e) => updatePort({ portType: e.target.value })}
            style={controlStyle}
          >
            <option value="input">Input (Target)</option>
            <option value="output">Output (Source)</option>
          </select>
        </div>

        {/* Position — editable */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Position</div>
          <select
            value={handle.position || (handleType === 'target' ? 'left' : 'right')}
            onChange={(e) => updatePort({ position: e.target.value })}
            style={controlStyle}
          >
            {POSITIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* Offset — separates handles sharing the same side */}
        {(() => {
          const pos = handle.position || (handleType === 'target' ? 'left' : 'right');
          const isHorizontalSide = pos === 'left' || pos === 'right';
          const offsetField = isHorizontalSide ? 'offsetY' : 'offsetX';
          const currentOffset = isHorizontalSide ? (handle.offsetY ?? 0) : (handle.offsetX ?? 0);
          const hint = isHorizontalSide
            ? 'Vertical offset (px) — separates handles on left/right'
            : 'Horizontal offset (px) — separates handles on top/bottom';
          return (
            <div className="property-row">
              <div className={`property-label ${themeClass}`} title={hint}>
                Offset {isHorizontalSide ? 'Y' : 'X'} (px)
              </div>
              <input
                type="number"
                style={{ ...controlStyle, cursor: 'text' }}
                value={currentOffset}
                step={10}
                onChange={(e) => updatePort({ [offsetField]: Number(e.target.value) })}
              />
              <div style={{ fontSize: '10px', color: theme === 'dark' ? 'rgba(150,160,155,0.7)' : '#9ca3af', marginTop: '3px' }}>
                {hint}
              </div>
            </div>
          );
        })()}

        {/* Handle Style — editable */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Handle Style</div>
          <select
            value={effectiveShape}
            onChange={(e) => updatePort({ handleStyle: e.target.value })}
            style={controlStyle}
          >
            {HANDLE_STYLES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {handle.isMainPort !== undefined && (
          <div className="property-row">
            <div className={`property-label ${themeClass}`}>Main Port</div>
            <div className={`property-value ${themeClass}`}>
              {handle.isMainPort ? '✓ Yes' : '✗ No'}
            </div>
          </div>
        )}

        {handle.isRequired !== undefined && (
          <div className="property-row">
            <div className={`property-label ${themeClass}`}>Required</div>
            <div className={`property-value ${themeClass}`}>
              {handle.isRequired ? '✓ Yes' : '✗ No'}
            </div>
          </div>
        )}

        <div className="property-row">
          <div className={`property-label ${themeClass}`}>
            Connections ({connections.length})
          </div>
          {connections.length > 0 ? (
            <div>
              {connectedNodes.map((conn, index) => (
                <div key={index} className={`connection-item ${themeClass}`}>
                  <div className="connection-item-name">
                    {conn.name}
                  </div>
                  <div className="connection-item-detail">
                    {handleType === 'source' ? 'To' : 'From'} handle: {conn.handleId || 'main'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`property-empty-state ${themeClass}`}>No connections</div>
          )}
        </div>
      </div>
    </div>
  );
};
